import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

const FiltersContext = createContext();


export const useFilters = () => {
	const context = useContext(FiltersContext);
	if (!context) {
		throw new Error('useFilters must be used within a FiltersProvider');
	}
	return context;
};

export const FiltersProvider = ({ children }) => {
	const [searchParams] = useSearchParams();
	const location = useLocation(); 

	// Stati per i filtri, separati dai query params durante l'uso
	const [selectedSettore, setSelectedSettore] = useState(null);
	const [selectedSede, setSelectedSede] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [selectedStructure, setSelectedStructure] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [isInitialized, setIsInitialized] = useState(false);

	// Ref per tracciare se stiamo navigando (stato diverso dal cambio filtro in pagina)
	const isNavigatingRef = useRef(false);
	const lastLocationRef = useRef(location.pathname);
	const wasNavigatingRef = useRef(false);

	// Aggiornamento silenzioso dell'URL senza causare re-render
	const updateUrlSilently = (newParams) => {
		const currentParams = new URLSearchParams(window.location.search);

		// Aggiorna i parametri
		Object.entries(newParams).forEach(([key, value]) => {
			if (value && value !== '') {
				currentParams.set(key, value);
			} else {
				currentParams.delete(key);
			}
		});

		const newSearch = currentParams.toString();
		const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
		window.history.replaceState(null, '', newUrl);
	};

	
	useEffect(() => {
		if (lastLocationRef.current !== location.pathname) {
			// Significa che stiamo navigando e non solo cambiando i filtri
			isNavigatingRef.current = true;
			wasNavigatingRef.current = true;
			lastLocationRef.current = location.pathname;
		}
	}, [location.pathname]);


	// Inizializza i filtri dai query params solo durante la navigazione
	useEffect(() => {
		if (!isNavigatingRef.current && isInitialized) {
			return; 
		}

		const categoryParam = searchParams.get('categoryid');
		const settoreParam = searchParams.get('settore');
		const sedeParam = searchParams.get('sede');
		const structureParam = searchParams.get('structure');
		const pageParam = searchParams.get('page');


		if (categoryParam || settoreParam || structureParam || sedeParam || pageParam || !isInitialized) {
			if (categoryParam) {
				const currentLang = window.location.pathname.split('/')[1]; 
				Promise.all([
					fetch(`/${currentLang}/contents/labtypes`).then(res => res.json()),
					fetch(`/${currentLang}/contents/sublabtypes`).then(res => res.json())
				])
				.then(([labTypes, subLabTypes]) => { 
					const allCategories = [...labTypes, ...subLabTypes];
					const matchingCategory = allCategories.find(cat => cat.value === categoryParam);
					
					if (matchingCategory) {
						setSelectedCategory({
							value: categoryParam,
							label: matchingCategory.label
						});
					} else {
						// Fallback if category not found
						setSelectedCategory({
							value: categoryParam,
							label: categoryParam
						});
					}
				})
				.catch(() => { 
					setSelectedCategory({
						value: categoryParam,
						label: categoryParam
					});
				});
			} else if (isNavigatingRef.current) {
				setSelectedCategory(null);
			}

			if (settoreParam) {
				setSelectedSettore({
					value: settoreParam,
					label: settoreParam
				});
			} else if (isNavigatingRef.current) {
				setSelectedSettore(null);
			}

			if (sedeParam) {
				setSelectedSede({
					value: sedeParam,
					label: sedeParam
				});
			} else if (isNavigatingRef.current) {
				setSelectedSede(null);
			}

			if (structureParam) {
				setSelectedStructure({
					value: structureParam,
					label: structureParam
				});
			} else if (isNavigatingRef.current) {
				setSelectedStructure(null);
			}

			if (pageParam) {
				const parsedPage = parseInt(pageParam);
				setCurrentPage(isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage);
			} else if (isNavigatingRef.current) {
				setCurrentPage(1);
			}
		}

		setIsInitialized(true);
		isNavigatingRef.current = false;  
	}, [searchParams, location.pathname]);


	// Scroll alla sezione dei filtri quando facciamo back e ci sono query params
	useEffect(() => {
		if (isInitialized && wasNavigatingRef.current &&
			(searchParams.get('categoryid') || searchParams.get('settore') || searchParams.get('structure') ||
				searchParams.get('sede') || searchParams.get('page'))) {

			setTimeout(() => {
				const filterElement = document.getElementById('map-filters');
				const header = document.querySelector('header');
				if (filterElement && header) {
					window.scrollTo({
						top: filterElement.offsetTop - header.offsetHeight,
						behavior: "smooth"
					});
				}
				wasNavigatingRef.current = false;
			}, 1000);
		}
	}, [isInitialized, searchParams]);


	// Funzione per aggiornare il settore
	const updateSettore = (settore) => {
		setSelectedSettore(settore);
		setCurrentPage(1);

		updateUrlSilently({
			settore: settore?.value || '',
			page: '1'
		});
	};

	// Funzione per aggiornare la sede
	const updateSede = (sede) => {
		setSelectedSede(sede);
		setCurrentPage(1);

		updateUrlSilently({
			sede: sede?.value || '',
			page: '1'
		});
	};

	// Funzione per aggiornare la categoria
	const updateCategory = (category) => {
		setSelectedCategory(category);
		setCurrentPage(1);

		updateUrlSilently({
			categoryid: category?.value || '',
			page: '1'
		});
	};

	const updateStructure = (structure) => {
		setSelectedStructure(structure);
		setCurrentPage(1);

		updateUrlSilently({
			structure: structure?.value || '',
			page: '1'
		});
	};

	// Funzione per aggiornare la paginazione
	const updatePage = (page) => {
		const validPage = Math.max(1, parseInt(page) || 1);
		setCurrentPage(validPage);

		updateUrlSilently({
			page: validPage.toString()
		});
	};

	// Funzione per resettare tutti i filtri
	const resetFilters = () => {
		setSelectedSettore(null);
		setSelectedSede(null);
		setSelectedCategory(null);
		setSelectedStructure(null);
		setCurrentPage(1);

		window.history.replaceState(null, '', location.pathname);
	};


	const getApiParams = () => {
		let parameters = new URLSearchParams();

		if (selectedCategory?.value) {
			parameters.append("categoryid", selectedCategory.value);
		}

		// Aggiungi tutti i tags in una volta sola per supportare filtri multipli
		const tags = [];
		if (selectedSettore?.value) {
			tags.push(selectedSettore.value);
		}
		if (selectedSede?.value) {
			tags.push(selectedSede.value);
		}
		
		// Append each tag as a separate parameter with the same key
		tags.forEach(tag => {
			parameters.append("tags", tag);
		});

		if (selectedStructure?.value) {
			parameters.append("structure", selectedStructure.value);
		}

		if (currentPage > 1) {
			parameters.append("startFrom", currentPage);
		}

		return parameters;
	};

	const value = {
		// Stati
		selectedSettore,
		selectedSede,
		selectedCategory,
		selectedStructure,
		currentPage,
		isInitialized,

		// Setters
		updateSettore,
		updateSede,
		updateCategory,
		updateStructure,
		updatePage,
		resetFilters,

		// Utility
		getApiParams
	};

	return (
		<FiltersContext.Provider value={value}>
			{children}
		</FiltersContext.Provider>
	);
};